import { useSelector, useDispatch } from 'react-redux'
import { useCallback, useMemo } from 'react'
import { api } from '../services/api'
import { showSuccess, showError } from '../actions/misc/misc.actions'
import moment from 'moment'
import { TRAINING_PAGE_IDS, FACILITY_VISIT_RELATED_TRAINING, TRAINING_CLASS } from '../utils'
import { updateFacilityIndividualAsync } from '../actions/facilities/facilities.actions'

const useSelectedFacility = () => {
	const dispatch = useDispatch()
	const facility = useSelector((state) => state.specific.facilities.selected)
	// console.log('facility', facility)
	const loading = useSelector((state) => state.specific.facilities.loading)
	const training = useSelector((state) => state.specific.facilities.training)
	const allStaff = useSelector((state) => state.specific.facilities.staff)
	const availableCubbies = useSelector((state) => state.specific.facilities.availableCubbies)

	const availableCubbiesForSelectedFacility = useMemo(() => {
		if (!facility || !availableCubbies) {
			return {}
		}

		return availableCubbies[facility._id] || {}
	}, [facility, availableCubbies])

	const updateFeaturedAddon = useCallback(
		async ({ addon_id, service_line }) => {
			try {
				const featured_addons = facility?.featured_addons ? [...facility.featured_addons] : []
				const serviceLineAddon = featured_addons.findIndex((a) => a.service_line === service_line)
				if (serviceLineAddon > -1) {
					featured_addons[serviceLineAddon] = { addon_id, service_line }
				} else {
					featured_addons.push({ addon_id, service_line })
				}

				await updateFacilityIndividualAsync(
					dispatch,
					facility._id,
					{ featured_addons },
					'Featured Enrichment Added'
				)
			} catch (e) {}
		},
		[facility, dispatch]
	)

	const removeFeaturedAddon = useCallback(
		async ({ addon_id }) => {
			try {
				const featured_addons = facility?.featured_addons?.filter((a) => a.addon_id !== addon_id) || []

				await updateFacilityIndividualAsync(
					dispatch,
					facility._id,
					{ featured_addons },
					'Featured Enrichment Removed'
				)
			} catch (e) {}
		},
		[facility, dispatch]
	)

	const getAvailableCubbiesForFacility = useCallback(
		async (date) => {
			dispatch({ type: 'GET_FACILITIES' })
			let data = {}

			// console.log('fac in here', facility)

			let url = `/facilities/${facility._id}/availableCubbies`

			if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
				url = `${url}?date=${date}`
			}

			try {
				const response = await api.get(url)
				if (response?.data?.data && response.data.data.length) {
					data = response.data.data[0]
				}
				if (response?.data?.errors && response.data.errors.length) {
					showError(response.data.errors[0])
				}
			} catch (e) {
				dispatch({ type: 'GET_FACILITIES_FAIL' })
			}

			return data
		},
		[dispatch, facility]
	)

	const trainingServicesSetup = facility?.services?.training || null

	const trainingStaff = useMemo(() => {
		if (!allStaff) {
			return []
		}

		const staff = allStaff
			.filter((staff) => {
				const { position = '', trainings_completed = [] } = staff
				return position === 'Dog Trainer' || trainings_completed.includes('Dog Training')
			})
			.map((staff) => {
				return {
					name: staff.full_name,
					_id: staff._id
				}
			})

		return staff
	}, [facility, allStaff])

	const trainingLocations = useMemo(() => {
		if (!facility) {
			return []
		}
		return training[facility._id]?.locations || []
	}, [facility, training])

	const trainingCourses = useMemo(() => {
		if (!facility) {
			return []
		}
		return training[facility._id]?.courses || []
	}, [facility, training])

	const trainingClasses = useMemo(() => {
		if (!facility) {
			return []
		}
		return training[facility._id]?.classes || []
	}, [facility, training])

	const trainingSchedule = useMemo(() => {
		if (!facility) {
			return []
		}
		return training[facility._id]?.schedule || []
	}, [facility, training])

	const trainingCalendar = useMemo(() => {
		if (!facility) {
			return []
		}
		return training[facility._id]?.calendar || []
	}, [facility, training])

	const updateFacilityServicesByServiceLine = useCallback(
		async ({ service_line, updates, facility_id }) => {
			if (!service_line || !updates || facility_id !== facility._id) {
				return
			}
			const payload = {
				success: false
			}
			try {
				dispatch({ type: 'UPDATE_FACILITY' })
				const { data } = await api.patch(`/facilities/${facility._id}/services/${service_line}`, updates)
				if (data.data && data.data.length > 0) {
					dispatch({ type: 'UPDATE_FACILITY_SUCCESS', payload: data.data[0] })
					dispatch(showSuccess(`Facility ${service_line} services updated!`))
					payload.success = true
				}
				if (data.errors && data.errors.length > 0) {
					payload.errors = data.errors
					dispatch({
						type: 'UPDATE_FACILITY_FAIL',
						payload: data.errors[0]
					})
				}
			} catch (e) {
				const error = e && e.response && e.response.data ? e.response.data : null
				if (error) {
					dispatch(showError(error))
				}
				dispatch({
					type: 'UPDATE_FACILITY_FAIL',
					payload: error || 'An error occured while updating the selected Facility.'
				})
			} finally {
				return payload
			}
		},
		[facility]
	)

	// Training Classes
	const getTrainingClassForFacility = useCallback(
		async ({ class_id }) => {
			if (!facility || !class_id) {
				return
			}
			dispatch({ type: 'GET_FACILITY_TRAINING', payload: true })
			try {
				const { data } = await api.get(`/facilities/${facility._id}/services/training/class/${class_id}`)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'ADD_FACILITY_TRAINING_CLASS',
						payload: { trainingClass: data.data[0], facilityId: facility._id }
					})
					return data.data[0]
				}
				if (data.errors && data.errors.length) {
					dispatch(showError(data.errors[0]))
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
					return null
				}
			} catch (e) {
				console.log('catch', e)
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
				return null
			}
		},
		[dispatch, facility]
	)

	/**
	 * API will return 3 months of classes based on the month and year provided, month of, month before, month after
	 */
	const getTrainingClassCalendarForMonthAndYearForFacility = useCallback(
		async ({ month, year }) => {
			if (!facility) {
				return
			}

			dispatch({ type: 'GET_FACILITY_TRAINING', payload: true })

			try {
				const tempmonth = month || moment().format('MM')
				const tempyear = year || moment().format('YYYY')

				const regularClassCalendar = await api.get(
					`/facilities/${facility._id}/services/training/classes/calendar?month=${tempmonth}&year=${tempyear}`
				)

				const fvrtSessionCalendar = await api.get(
					`/facilities/${facility._id}/services/training/facility_visit_related_sessions/calendar?month=${tempmonth}&year=${tempyear}`
				)

				const [regular, fvrt] = await Promise.all([regularClassCalendar, fvrtSessionCalendar])

				const { data: regularData } = regular
				const { data: fvrtData } = fvrt

				const events = {}

				if (regularData.data && Array.isArray(regularData.data)) {
					for (const event of regularData.data) {
						const { dates, adjustments = [] } = event
						for (const date of dates) {
							const indexEvent = { ...event, type: TRAINING_CLASS }

							// this handles the mapping of dates when an adjustment exists
							const hasAdjustment = adjustments.find((adj) => adj.session_date === date)
							let indexDate = date
							if (hasAdjustment) {
								indexEvent.start_time = hasAdjustment.new_start_time
									? hasAdjustment.new_start_time
									: event.start_time
								indexEvent.end_time = hasAdjustment.new_end_time
									? hasAdjustment.new_end_time
									: event.end_time
							}
							if (!events[indexDate]) {
								events[indexDate] = []
							}
							events[indexDate].push(indexEvent)
						}
					}
				}
				if (fvrtData.data && Array.isArray(fvrtData.data)) {
					for (const fvrtAddon of fvrtData.data) {
						const { date } = fvrtAddon
						if (!events[date]) {
							events[date] = []
						}
						events[date].push({ ...fvrtAddon, type: FACILITY_VISIT_RELATED_TRAINING })
					}
				}

				if (Object.keys(events).length > 0) {
					dispatch({
						type: 'SET_FACILITY_TRAINING',
						payload: { data: events, trainingKey: 'calendar', facilityId: facility._id }
					})
				}

				if (regularData.errors && regularData.errors.length) {
					dispatch(showError(regularData.errors[0]))
					dispatch({
						type: 'GET_FACILITY_TRAINING_FAILED',
						payload: regularData.errors[0]
					})
				}
				if (fvrtData.errors && fvrtData.errors.length) {
					dispatch(showError(fvrtData.errors[0]))
					dispatch({
						type: 'GET_FACILITY_TRAINING_FAILED',
						payload: fvrtData.errors[0]
					})
				}
			} catch (e) {
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
			}
		},
		[facility, dispatch]
	)

	const getTrainingClassNextAvailableId = useCallback(
		async ({ course_id }) => {
			let nextId = '<###>'
			if (!course_id || !facility) {
				return nextId
			}
			try {
				const { data } = await api.get(
					`facilities/${facility._id}/services/training/classes/next_available_id?course_id=${course_id}`
				)
				if (data.data && Array.isArray(data.data)) {
					nextId = data.data[0]
				}
				if (data.errors && data.errors.length) {
					nextId = '<Error>'
				}
			} catch (e) {
				nextId = '<Error>'
			} finally {
				return nextId
			}
		},
		[facility]
	)

	const getTrainingClassScheduleForDateForFacility = useCallback(
		async (date) => {
			if (!facility) {
				return
			}
			dispatch({ type: 'GET_FACILITY_TRAINING', payload: true })
			try {
				const tempdate = date || moment().format('YYYY-MM-DD')

				const regularClassSchedule = await api.get(
					`/facilities/${facility._id}/services/training/classes/schedule?date=${tempdate}`
				)

				const fvrtSessionSchedule = await api.get(
					`/facilities/${facility._id}/services/training/facility_visit_related_sessions/schedule?date=${tempdate}`
				)

				const [regular, fvrt] = await Promise.all([regularClassSchedule, fvrtSessionSchedule])

				const { data: regularData } = regular
				const { data: fvrtData } = fvrt

				let schedule = []

				if (regularData.data) {
					schedule = [...schedule, ...regularData.data]
				}
				if (fvrtData.data) {
					schedule = [...schedule, ...fvrtData.data]
				}

				dispatch({
					type: 'SET_FACILITY_TRAINING',
					payload: { data: schedule, trainingKey: 'schedule', facilityId: facility._id }
				})
				if (regularData.errors && regularData.errors.length) {
					dispatch(showError(regularData.errors[0]))
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: regularData.errors[0] })
				}
				if (fvrtData.errors && fvrtData.errors.length) {
					dispatch(showError(fvrtData.errors[0]))
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: fvrtData.errors[0] })
				}
			} catch (e) {
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
			}
		},
		[facility, dispatch]
	)

	const saveTrainingClassForFacility = useCallback(
		async ({ trainingClass, facility_id }) => {
			const payload = {
				success: false,
				data: [],
				errors: []
			}

			if (!facility || !trainingClass || facility_id !== facility._id) {
				payload.errors = ['Invalid parameters']
				return payload
			}

			try {
				const { data } = await api.post(
					`/facilities/${facility._id}/services/training/class`,
					trainingClass
				)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'NEW_FACILITY_TRAINING_CLASS',
						payload: { trainingClass: data.data[0], facilityId: facility._id }
					})
					payload.success = true
					payload.data = data.data[0]
				}
				if (data.errors && data.errors.length) {
					payload.errors = data.errors
				}
			} catch (e) {
				payload.errors = e.data?.errors || e.message || ['An error occurred while saving the class']
			} finally {
				return payload
			}
		},
		[facility]
	)

	const updateTrainingClassForFacility = useCallback(
		async ({ mongo_class_id, updates, facility_id }) => {
			const payload = {
				success: false,
				data: [],
				errors: []
			}
			if (!facility || !updates || !mongo_class_id || facility_id !== facility._id) {
				payload.errors = ['Invalid parameters']
				return payload
			}

			try {
				const { data } = await api.patch(
					`/facilities/${facility._id}/services/training/class/${mongo_class_id}`,
					updates
				)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'UPDATE_FACILITY_TRAINING_CLASS',
						payload: { _id: mongo_class_id, update: data.data[0], facilityId: facility._id }
					})
					payload.success = true
					payload.data = data.data[0]
				}
				if (data.errors && data.errors.length) {
					payload.errors = data.errors
				}
			} catch (e) {
				payload.errors = e.data?.errors || e.message || ['An error occurred while saving the class']
			} finally {
				return payload
			}
		},
		[facility]
	)

	const cancelTrainingClassForFacility = useCallback(
		async ({ mongo_class_id, facility_id }) => {
			const payload = {
				success: false,
				data: [],
				errors: []
			}
			if (!facility || !mongo_class_id || facility_id !== facility._id) {
				payload.errors = ['Invalid parameters']
				return payload
			}

			try {
				const { data } = await api.patch(
					`/facilities/${facility._id}/services/training/class/${mongo_class_id}/cancel`,
					{}
				)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'UPDATE_FACILITY_TRAINING_CLASS',
						payload: { _id: mongo_class_id, update: data.data[0], facilityId: facility._id }
					})
					payload.success = true
					payload.data = data.data[0]
				}
				if (data.errors && data.errors.length) {
					payload.errors = data.errors
				}
			} catch (e) {
				payload.errors = e.data?.errors || e.message || ['An error occurred while cancelling the class']
			} finally {
				return payload
			}
		},
		[facility]
	)

	const updateTrainingClassSession = useCallback(
		async ({ mongo_class_id, updates, facility_id, session_number }) => {
			const payload = {
				success: false,
				data: [],
				errors: []
			}
			if (!facility || !updates || !mongo_class_id || facility_id !== facility._id) {
				payload.errors = ['Invalid parameters']
				return payload
			}

			try {
				const { data } = await api.patch(
					`/facilities/${facility._id}/services/training/class/${mongo_class_id}/session/${session_number}`,
					updates
				)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'UPDATE_FACILITY_TRAINING_CLASS',
						payload: { _id: mongo_class_id, update: data.data[0], facilityId: facility._id }
					})
					payload.success = true
					payload.data = data.data[0]
				}
				if (data.errors && data.errors.length) {
					payload.errors = data.errors
				}
			} catch (e) {
				payload.errors = e.data?.errors || e.message || ['An error occurred while saving the class']
			} finally {
				return payload
			}
		},
		[facility]
	)

	// Training Courses
	const getTrainingCoursesForFacility = useCallback(async () => {
		if (!facility) {
			return
		}
		dispatch({ type: 'GET_FACILITY_TRAINING', payload: true })
		try {
			const { data } = await api.get(`/facilities/${facility._id}/services/training/courses`)
			if (data.data && Array.isArray(data.data)) {
				dispatch({
					type: 'SET_FACILITY_TRAINING',
					payload: { data: data.data, trainingKey: 'courses', facilityId: facility._id }
				})
			}
			if (data.errors && data.errors.length) {
				dispatch(showError(data.errors[0]))
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
			}
		} catch (e) {
			dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
		}
	}, [facility, dispatch])

	const saveTrainingCourseForFacility = useCallback(
		async ({ course, facility_id }) => {
			if (!facility || !course || facility_id !== facility._id) {
				return
			}
			dispatch({ type: 'GET_FACILITY_TRAINING', payload: true })
			try {
				const { data } = await api.post(`/facilities/${facility._id}/services/training/course`, course)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'ADD_FACILITY_TRAINING_COURSE',
						payload: { course: data.data, facilityId: facility._id }
					})
				}
				if (data.errors && data.errors.length) {
					dispatch(showError(data.errors[0]))
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
				}
			} catch (e) {
				let message = e.message || 'An error occurred while saving the course.'
				if (e.data && e.data.errors && e.data.errors.length) {
					message = e.data.errors[0]
				}
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: message })
				dispatch(showError(message))
			}
		},
		[facility]
	)

	const updateTrainingCourseForFacility = useCallback(
		async ({ course_id, updates, facility_id }) => {
			if (!facility || !updates || !course_id || facility_id !== facility._id) {
				return
			}
			dispatch({ type: 'GET_FACILITY_TRAINING', payload: true })
			try {
				const { data } = await api.patch(
					`/facilities/${facility._id}/services/training/course/${course_id}`,
					updates
				)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'UPDATE_FACILITY_TRAINING_COURSE',
						payload: { course_id, update: data.data[0], facilityId: facility._id }
					})
				}
				if (data.errors && data.errors.length) {
					dispatch(showError(data.errors[0]))
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
				}
			} catch (e) {
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
			}
		},
		[facility]
	)

	// Training Locations
	const getTrainingLocationsForFacility = useCallback(async () => {
		if (!facility) {
			return
		}
		dispatch({ type: 'GET_FACILITY_TRAINING', payload: true })
		try {
			const { data } = await api.get(`/facilities/${facility._id}/services/training/locations`)
			if (data.data && Array.isArray(data.data)) {
				dispatch({
					type: 'SET_FACILITY_TRAINING',
					payload: { data: data.data, trainingKey: 'locations', facilityId: facility._id }
				})
			}
			if (data.errors && data.errors.length) {
				dispatch(showError(data.errors[0]))
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
			}
		} catch (e) {
			dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
		}
	}, [facility, dispatch])

	const saveTrainingLocationForFacility = useCallback(
		async ({ location, facility_id }) => {
			if (!facility || !location || facility_id !== facility._id) {
				return
			}
			dispatch({ type: 'GET_FACILITY_TRAINING', payload: true })
			try {
				const { data } = await api.post(`/facilities/${facility._id}/services/training/location`, location)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'ADD_FACILITY_TRAINING_LOCATION',
						payload: { location: data.data, facilityId: facility._id }
					})
				}
				if (data.errors && data.errors.length) {
					dispatch(showError(data.errors[0]))
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
				}
			} catch (e) {
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
			}
		},
		[facility]
	)

	const updateTrainingLocationForFacility = useCallback(
		async ({ location_id, updates, facility_id }) => {
			if (!facility || !updates || !location_id || facility_id !== facility._id) {
				return
			}
			dispatch({ type: 'GET_FACILITY_TRAINING', payload: true })
			try {
				const { data } = await api.patch(
					`/facilities/${facility._id}/services/training/location/${location_id}`,
					updates
				)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'UPDATE_FACILITY_TRAINING_LOCATION',
						payload: { location_id, update: data.data[0], facilityId: facility._id }
					})
				}
				if (data.errors && data.errors.length) {
					dispatch(showError(data.errors[0]))
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
				}
			} catch (e) {
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
			}
		},
		[facility]
	)

	// Training Enrollments
	const saveEnrollmentForClass = useCallback(
		async ({ facility_id, enrollment }) => {
			if (!facility || !enrollment || !enrollment.training_class_id || facility_id !== facility._id) {
				return
			}
			dispatch({ type: 'GET_FACILITY_TRAINING', payload: true })
			const payload = {
				success: false,
				data: [],
				errors: []
			}
			try {
				const { data } = await api.post(
					`/facilities/${facility._id}/services/training/class/${enrollment.training_class_id}/enrollment/create`,
					enrollment
				)
				if (data.data && data.data.length > 0) {
					payload.success = true
					payload.data = data.data[0]
					dispatch({
						type: 'UPDATE_FACILITY_TRAINING_CLASS',
						payload: {
							_id: enrollment.training_class_id,
							update: data.data[0],
							facilityId: facility_id
						}
					})
				}
				if (data.errors && data.errors.length) {
					payload.success = false
					payload.errors = data.errors
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
				}
			} catch (e) {
				payload.success = false
				payload.errors = e.data?.errors || e.message || ['An error occurred while saving the enrollment.']
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
			} finally {
				return payload
			}
		},
		[facility]
	)

	const cancelEnrollmentForClass = useCallback(
		async ({ facility_id, mongo_class_id, handler_ids, pet_id }) => {
			if (!facility || !mongo_class_id || !handler_ids || !pet_id || facility_id !== facility._id) {
				return
			}
			try {
				const { data } = await api.patch(
					`/facilities/${facility._id}/services/training/class/${mongo_class_id}/enrollment/cancel`,
					{ handler_ids, pet_id }
				)
				if (data.data && data.data.length > 0) {
					dispatch({
						type: 'UPDATE_FACILITY_TRAINING_CLASS',
						payload: {
							_id: mongo_class_id,
							update: data.data[0],
							facilityId: facility_id
						}
					})
				}
				if (data.errors && data.errors.length) {
					dispatch(showError(data.errors[0]))
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
				}
			} catch (e) {
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
			}
		},
		[facility]
	)

	const getEnrollmentForClass = useCallback(
		async ({ facility_id, enrollment_id }) => {
			if (!facility || !enrollment_id || facility_id !== facility._id) {
				return
			}
			const payload = {
				success: false,
				data: [],
				errors: []
			}

			try {
				const { data } = await api.get(
					`/facilities/${facility._id}/services/training/enrollment/${enrollment_id}`
				)
				if (data.data && data.data.length > 0) {
					payload.success = true
					payload.data = data.data
				}
				if (data.errors && data.errors.length) {
					payload.success = false
					payload.errors = data.errors
					dispatch(showError(data.errors[0]))
				}
			} catch (e) {
				payload.success = false
				payload.errors = e.data?.errors || e.message || ['An error occurred retrieving the enrollment.']
				dispatch(showError(payload.errors[0]))
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
			} finally {
				return payload
			}
		},
		[facility]
	)

	const updateFVRTSession = useCallback(
		async ({ addon_id: session_id, updates }) => {
			if (!session_id || !updates || !facility) {
				return
			}
			const payload = {
				success: false,
				data: [],
				errors: []
			}

			try {
				// These are just enrichments now, but using session_id in anticipation of future changes
				// For now session_id = addon_id
				const { data } = await api.patch(
					`/facilities/${facility._id}/services/training/facility_visit_related_sessions/${session_id}`,
					updates
				)
				if (data && data.data && data.data.length > 0) {
					payload.success = true
					payload.data = data.data[0]
					const { date, _id, facility_id, instructor_id, time_scheduled } = data.data[0]
					const reduxUpdate = {}
					if (instructor_id || instructor_id === null || instructor_id === '') {
						reduxUpdate.instructor_id = instructor_id
					}
					if (time_scheduled || time_scheduled === null || time_scheduled === '') {
						reduxUpdate.time_scheduled = time_scheduled
					}
					dispatch({
						type: 'UPDATE_FACILITY_TRAINING_CALENDAR_FVRT_SESSION',
						payload: { date, _id, facility_id, update: reduxUpdate }
					})
				}
			} catch (e) {
				payload.success = false
				payload.errors = e.data?.errors ||
					e.message || ['An error occurred updating Play & Study session.']
				dispatch(showError(payload.errors[0]))
			} finally {
				return payload
			}
		},
		[dispatch, facility]
	)

	/**
	 * Get households with training enrollments
	 * @param {{
	 * 	facility_id: String,
	 * 	search: String,
	 * 	additionalSearchParams: [
	 * 		['searchBy', 'contact' | 'pet']
	 * 		['excludeTrainingClassId', String],
	 * 		['trainingCourseId', String],
	 * 	]}} param0
	 * @returns {{success: boolean, data: [], errors: []}}
	 */
	const getHouseholdsWithTrainingEnrollments = useCallback(
		async ({ facility_id, search, additionalSearchParams = [] }) => {
			if (!facility || facility_id !== facility._id) {
				return
			}
			const payload = {
				success: false,
				data: [],
				errors: []
			}
			try {
				let url = `/clients/search/training/enrollment`
				if (search) {
					url += `?text=${search}&facility_id=${facility._id}`
				}
				if (additionalSearchParams.length) {
					for (const paramSet of additionalSearchParams) {
						const [key, value] = paramSet
						url += `&${key}=${value}`
					}
				}
				const response = await api.get(url)
				if (response.data.errors && response.data.errors.length) {
					payload.errors = response.data.errors
				}
				if (response.data.data) {
					payload.data = response.data.data
					payload.success = true
				}
			} catch (e) {
				payload.errors = e
			} finally {
				return payload
			}
		},
		[facility]
	)

	// Training Attendance
	const saveAttendaceStatusForClass = useCallback(
		async ({
			facility_id,
			mongo_class_id,
			training_attendance_id,
			pet_id,
			status_id,
			session,
			source = TRAINING_PAGE_IDS.ScheduleView
		}) => {
			if (
				!facility ||
				!mongo_class_id ||
				!training_attendance_id ||
				!pet_id ||
				!status_id ||
				facility_id !== facility._id
			) {
				return
			}
			try {
				const { data } = await api.patch(
					`/facilities/${facility._id}/services/training/class/${mongo_class_id}/attendance/${training_attendance_id}`,
					{ status_id }
				)
				if (data.data && data.data.length > 0) {
					if (source === TRAINING_PAGE_IDS.ScheduleView) {
						dispatch({
							type: 'UPDATE_FACILITY_TRAINING_SCHEDULE_ATTENDANCE',
							payload: {
								attendanceId: training_attendance_id,
								status: data.data[0],
								facilityId: facility_id
							}
						})
					}
					if (source === TRAINING_PAGE_IDS.ClassView) {
						dispatch({
							type: 'UPDATE_FACILITY_TRAINING_CLASS_SESSION_ATTENDANCE',
							payload: {
								classId: mongo_class_id,
								attendanceId: training_attendance_id,
								facilityId: facility_id,
								session,
								status: data.data[0]
							}
						})
					}
				}
				if (data.errors && data.errors.length) {
					dispatch(showError(data.errors[0]))
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
				}
			} catch (e) {
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
			}
		},
		[facility]
	)

	const saveAttendanceTransferForClass = useCallback(
		async ({ facility_id, mongo_class_id, transfers }) => {
			if (!facility || !mongo_class_id || !transfers) {
				return
			}

			const payload = {
				success: false,
				data: [],
				errors: []
			}

			try {
				const { data } = await api.post(
					`/facilities/${facility._id}/services/training/class/${mongo_class_id}/attendance/transfer`,
					{ transfers }
				)
				if (data.data && data.data.length > 0) {
					payload.success = true
					payload.data = data.data[0]
					dispatch({
						type: 'UPDATE_FACILITY_TRAINING_CLASS',
						payload: {
							_id: mongo_class_id,
							update: data.data[0],
							facilityId: facility_id
						}
					})
				}
				if (data.errors && data.errors.length) {
					payload.errors = data.errors
					dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: data.errors[0] })
				}
			} catch (e) {
				payload.errors = e.data?.errors ||
					e.message || ['An error occurred while saving the attendance transfer.']
				dispatch({ type: 'GET_FACILITY_TRAINING_FAILED', payload: e.message })
			} finally {
				return payload
			}
		},
		[facility]
	)

	const runAutoAssignCubbies = useCallback(
		async (date) => {
			if (!facility) {
				return null
			}

			const payload = {
				success: false,
				data: [],
				errors: []
			}

			try {
				const { data } = await api.post(`/facilities/autoAssignCubbies/${facility._id}`, { date })
				if (data.data && data.data.length > 0) {
					payload.success = true
					payload.data = data.data[0]
					dispatch(showSuccess('Auto Assign Complete.'))
				}
				if (data.errors && data.errors.length) {
					payload.errors = data.errors
					dispatch(showError(data.errors.join(', ')))
					dispatch({ type: 'AUTO_ASSIGN_CUBBIES_FAILED', payload: data.errors[0] })
				}
			} catch (e) {
				payload.errors = e.data?.errors ||
					e.message || ['An error occurred while running the auto assign process.']
				dispatch(showError(e.message))
				dispatch({ type: 'AUTO_ASSIGN_CUBBIES_FAILED', payload: e.message })
			}
			return payload
		},
		[facility]
	)

	return {
		facility,
		loading,
		trainingServicesSetup,
		trainingLocations,
		trainingCourses,
		trainingStaff,
		trainingClasses,
		trainingSchedule,
		trainingCalendar,
		updateFacilityServicesByServiceLine,
		getTrainingLocationsForFacility,
		saveTrainingLocationForFacility,
		updateTrainingLocationForFacility,
		getTrainingCoursesForFacility,
		saveTrainingCourseForFacility,
		updateTrainingCourseForFacility,
		getTrainingClassScheduleForDateForFacility,
		getTrainingClassCalendarForMonthAndYearForFacility,
		saveTrainingClassForFacility,
		getTrainingClassForFacility,
		updateTrainingClassForFacility,
		updateTrainingClassSession,
		getTrainingClassNextAvailableId,
		cancelTrainingClassForFacility,
		getEnrollmentForClass,
		saveEnrollmentForClass,
		cancelEnrollmentForClass,
		getHouseholdsWithTrainingEnrollments,
		saveAttendaceStatusForClass,
		saveAttendanceTransferForClass,
		updateFVRTSession,
		availableCubbiesForSelectedFacility,
		getAvailableCubbiesForFacility,
		runAutoAssignCubbies,
		updateFeaturedAddon,
		removeFeaturedAddon,
		allStaff
	}
}

export default useSelectedFacility
